import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  getAllExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
} from "../../../services/expenseCategoriesService";
import { ExpenseCategory, ExpenseCategoryCreatePayload, ExpenseCategoryUpdatePayload, PaginatedExpenseCategories } from "../../../types/expenseCategoryTypes";

export const useExpenseCategories = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<PaginatedExpenseCategories>({
    data: [],
    pagination: {
      total: 0,
      page: 1,
      limit: 10000
    }
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const fetchExpenseCategories = useCallback(async (filters: { [key: string]: string | number | null } = {}) => {
    setLoading(true);
    try {
      const response = await getAllExpenseCategories(filters);
      console.log("response", response);
      setCategories(response.data);
      setError(null);
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_fetch_categories"));
      setError(t("failed_to_fetch_categories"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCreate = async (payload: ExpenseCategoryCreatePayload) => {
    try {
      await createExpenseCategory(payload);
      message.success(t("expense_category_created_successfully"));
      await fetchExpenseCategories();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_create_expense_category"));
      return false;
    }
  };

  const handleUpdate = async (categoryId: string, payload: ExpenseCategoryUpdatePayload) => {
    try {
      await updateExpenseCategory(categoryId, payload);
      message.success(t("expense_category_updated_successfully"));
      await fetchExpenseCategories();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_update_expense_category"));
      return false;
    }
  };

  const handleDelete = async (categoryId: string) => {
    try {
      await deleteExpenseCategory(categoryId);
      message.success(t("expense_category_deleted_successfully"));
      await fetchExpenseCategories();
      return true;
    } catch (err) {
      console.error(err);
      // message.error(t("failed_to_delete_expense_category"));
      return false;
    }
  };

  return {
    categories: categories.data,
    infoPagination: {
      total: categories.pagination.total,
      page: categories.pagination.page,
      limit: categories.pagination.limit,
    },
    loading,
    error,
    selectedCategory,
    setSelectedCategory,
    isModalOpen,
    setIsModalOpen,
    fetchExpenseCategories,
    handleCreate,
    handleUpdate,
    handleDelete
  };
};

export default useExpenseCategories;
